import { useDialog } from "@opencode-ai/ui/context/dialog"
import { TooltipV2 } from "@opencode-ai/ui/v2/tooltip-v2"
import { makeEventListener } from "@solid-primitives/event-listener"
import { useLocation, useNavigate } from "@solidjs/router"
import { createEffect, For, on, Show, Suspense, untrack, type ParentProps } from "solid-js"
import { createStore } from "solid-js/store"
import { DialogDatoxActivityLogs } from "@/components/datox-activity-logs-dialog"
import { DialogDatoxUsage } from "@/components/datox-usage-dialog"
import { useSettingsCommand } from "@/components/settings-dialog"
import type { TitlebarUpdate } from "@/components/titlebar"
import { applyPath, backPath, forwardPath, type TitlebarHistory } from "@/components/titlebar-history"
import { useCommand } from "@/context/command"
import { useLanguage } from "@/context/language"
import { usePlatform } from "@/context/platform"
import { listCompanies, type DatoxCompany } from "@/datox/api"
import { activeCompanyID, localDevHeaders, useDatoxAuth } from "@/datox/auth-ui"
import { TablerIcon, type TablerName } from "@/datox/v2/tabler-icons"
import { DialogDatoxLogout } from "@/pages/home/datox-project-dialogs"
import { ownsTitle } from "@/datox/v2/brand-head"
import "@/datox/v2/tokens.css"

type Destination = { label: string; icon: TablerName; path: string; match?: (pathname: string) => boolean }

const TITLEBAR_HEIGHT = 36
const MAC_TRAFFIC_LIGHTS_WIDTH = 84
const WINDOWS_CONTROLS_WIDTH = 138
const RAIL_WIDTH = 48
const SIDEBAR_WIDTH = 220
// Below this the sidebar starts (and falls back to) the icon rail, whatever was remembered.
const WIDE = "(min-width: 1100px)"

// The new Lesha Bank app shell: a Compliance V2 sidebar (Bobur's labelled 220px menu) that collapses to the 48px icon rail.
// The four everyday places first, then "Workspace tools" as an inline disclosure with its three groups: outputs,
// preparation and operations, configuration. It stands in for Titlebar + DatoxNavigationShell, so it also keeps
// their commands (back / forward / home, settings), the titlebar mount hosts pages portal into, the updater and desktop chrome.
export function DatoxV2Shell(props: ParentProps<{ update?: TitlebarUpdate }>) {
  const language = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const platform = usePlatform()
  const command = useCommand()
  const prefix = (path: string) => (pathname: string) => pathname === path || pathname.startsWith(`${path}/`)
  const active = (item: Destination) => (item.match ?? prefix(item.path))(location.pathname)
  // Bobur's grouping: the four everyday places first, everything else folded under "Workspace tools".
  const daily = (): Destination[] => [
    { label: language.t("datox.v2.nav.reports"), icon: "file-analytics", path: "/reports", match: (pathname) => prefix("/reports")(pathname) || prefix("/factsheets")(pathname) },
    { label: language.t("datox.catalog.workflows"), icon: "git-branch", path: "/catalog/workflows" },
    { label: language.t("datox.suite.dataSources"), icon: "folder", path: "/files" },
    { label: language.t("datox.chats.title"), icon: "messages", path: "/chats" },
  ]
  const sections = (): { title: string; items: Destination[] }[] => [
    {
      title: language.t("datox.v2.nav.outputsGroup"),
      items: [
        // "All outputs" (/overview) is gone from the bank's menu: it redirects to Reports, the one list (UX research P7).
        { label: language.t("datox.v2.nav.reportFiles"), icon: "file-text", path: "/catalog/reports" },
        { label: language.t("datox.catalog.dashboards"), icon: "layout-dashboard", path: "/catalog/dashboards" },
        { label: language.t("datox.approvals.title"), icon: "checks", path: "/approvals" },
      ],
    },
    {
      title: language.t("datox.v2.nav.preparation"),
      items: [
        { label: language.t("home.projects"), icon: "folders", path: "/projects" },
        { label: language.t("datox.automations.title"), icon: "clock", path: "/automations" },
        { label: language.t("datox.integrations.title"), icon: "plug-connected", path: "/integrations" },
      ],
    },
    {
      title: language.t("datox.v2.nav.configuration"),
      items: [
        { label: language.t("datox.v2.nav.agents"), icon: "robot", path: "/agents" },
      ],
    },
  ]
  // Folded until opened, or while the current page is one of the tools.
  const inTools = () => sections().some((section) => section.items.some(active))
  const toolsOpen = () => menu.tools || inTools()
  const go = (path: string) => navigate(path)
  // The report editor (/reports/:projectID/:workspaceID) is a focused full-screen workspace (Artyom: «два меню и правда
  // выглядят не очень… в редакторе можно основное прятать»): the shell drops its rail there. The editor's own tool rail
  // carries the Lesha mark (to /reports) and this avatar menu, and its ← returns to the list with its filters.
  const focus = () => /^\/reports\/[^/]+\/[^/]+\/?$/.test(location.pathname)
  // The tab title on every rail page (DEMO-SWEEP 6.3): "<rail label> — Lesha Bank". Pages that name themselves own it.
  // Set after the route change settles, so a leaving page's cleanup (which restores its previous title) can't win.
  createEffect(on(() => location.pathname, (pathname) => {
    if (ownsTitle(pathname)) return
    queueMicrotask(() => {
      if (location.pathname !== pathname) return
      const item = [...daily(), ...sections().flatMap((section) => section.items)].find(active)
      document.title = item ? language.t("datox.v2.title.page", { page: item.label }) : language.t("datox.v2.title.app")
    })
  }))

  // Expanded or collapsed, remembered per user in this browser; narrow windows start (and fall back to) the rail.
  const auth = useDatoxAuth()
  const storageKey = () => `dx-v2-sidebar:${auth.state.accountEmail || localDevHeaders()["X-User-Email"] || localDevHeaders()["X-User-Id"] || "default"}`
  const remembered = () => {
    try {
      return localStorage.getItem(storageKey())
    } catch {
      return null
    }
  }
  const wide = () => typeof window.matchMedia !== "function" || window.matchMedia(WIDE).matches
  const [menu, setMenu] = createStore({ tools: false, expanded: wide() && remembered() !== "collapsed" })
  const expanded = () => menu.expanded
  const setExpanded = (value: boolean) => setMenu("expanded", value)
  // The account resolves after the first paint: its own remembered state wins once it is known.
  createEffect(on(storageKey, () => setExpanded(wide() && remembered() !== "collapsed"), { defer: true }))
  if (typeof window.matchMedia === "function") {
    const query = window.matchMedia(WIDE)
    makeEventListener(query, "change", () => setExpanded(query.matches && remembered() !== "collapsed"))
  }
  const toggleSidebar = () => {
    const next = !expanded()
    setExpanded(next)
    try {
      localStorage.setItem(storageKey(), next ? "expanded" : "collapsed")
    } catch {}
  }
  const railWidth = () => (expanded() ? SIDEBAR_WIDTH : RAIL_WIDTH)

  // Back / forward over the app's own path history, and mod+b home, as Titlebar registered them.
  const [history, setHistory] = createStore({ stack: [] as string[], index: 0, action: undefined as "back" | "forward" | undefined })
  createEffect(() => {
    const current = `${location.pathname}${location.search}${location.hash}`
    untrack(() => {
      const next = applyPath(history, current)
      if (next !== history) setHistory(next)
    })
  })
  const step = (next?: { state: TitlebarHistory; to: string }) => {
    if (!next) return
    setHistory(next.state)
    navigate(next.to)
  }
  command.register(() => [
    { id: "common.goBack", title: language.t("common.goBack"), category: language.t("command.category.view"), keybind: "mod+[", onSelect: () => step(backPath(history)) },
    { id: "common.goForward", title: language.t("common.goForward"), category: language.t("command.category.view"), keybind: "mod+]", onSelect: () => step(forwardPath(history)) },
  ])
  command.register("titlebar-home", () => [
    { id: "home.toggle", title: language.t("home.title"), category: language.t("command.category.view"), keybind: "mod+b", hidden: true, onSelect: () => go("/reports") },
  ])

  // Desktop chrome: the bar is the drag region; macOS traffic lights sit over the rail's top and the bar's start,
  // Windows caption buttons over the bar's end.
  const desktop = platform.platform === "desktop"
  const mac = desktop && platform.os === "macos"
  const windows = desktop && platform.os === "windows"
  const zoom = () => platform.webviewZoom?.() ?? 1
  const lights = () => mac && !platform.windowFullscreen?.()
  const updateVersion = () => props.update?.version()
  const installing = () => props.update?.installing() ?? false

  // One row for both states: icon + label; the collapsed rail clips the label and names the row in a tooltip instead.
  const rail = (item: Destination) => (
    <TooltipV2 placement="right" value={item.label} inactive={expanded()}>
      <button type="button" class="dx-v2-rail-button" aria-label={item.label} aria-current={active(item) ? "page" : undefined} onClick={() => go(item.path)}>
        <TablerIcon name={item.icon} size={16} stroke-width="1.5" />
        <span class="dx-v2-rail-label">{item.label}</span>
      </button>
    </TooltipV2>
  )

  return (
    <div class="dx-v2-shell" data-focus={focus() || undefined}>
      <Show when={!focus()}>
        <nav
          class="dx-v2-rail"
          data-expanded={expanded() || undefined}
          aria-label={language.t("datox.v2.nav")}
          style={lights() ? { "padding-top": `${TITLEBAR_HEIGHT / zoom()}px` } : undefined}
          data-tauri-drag-region={desktop || undefined}
        >
          <div class="dx-v2-rail-head">
            <a class="dx-v2-rail-logo" href="/reports" aria-label={language.t("lesha.logo.alt")} onClick={(event) => { event.preventDefault(); go("/reports") }}>
              <img class="dx-v2-rail-mark" src="/lesha-bank-mark.png" alt="" />
              <img class="dx-v2-rail-wordmark" src="/lesha-bank-logo.png" alt="" />
            </a>
            <TooltipV2 placement="right" value={language.t(expanded() ? "datox.v2.nav.collapse" : "datox.v2.nav.expand")}>
              <button type="button" class="dx-v2-rail-toggle" aria-label={language.t(expanded() ? "datox.v2.nav.collapse" : "datox.v2.nav.expand")} aria-expanded={expanded()} onClick={toggleSidebar}>
                <TablerIcon name="layout-sidebar-left-collapse" size={16} stroke-width="1.5" />
              </button>
            </TooltipV2>
          </div>
          <span class="dx-v2-rail-caption" data-lead="true">{language.t("datox.v2.nav.reporting")}</span>
          <For each={daily()}>{rail}</For>
          <span class="dx-v2-rail-sep" role="separator" />
          <TooltipV2 placement="right" value={language.t(toolsOpen() ? "datox.v2.nav.toolsHide" : "datox.v2.nav.toolsShow")} inactive={expanded()}>
            <button type="button" class="dx-v2-rail-button dx-v2-rail-tools" aria-label={language.t("datox.designer.workspaceTools")} aria-expanded={toolsOpen()} disabled={inTools()} onClick={() => setMenu("tools", !menu.tools)}>
              <TablerIcon class="dx-v2-rail-tools-rail" name={toolsOpen() ? "chevron-down" : "dots"} size={16} stroke-width="1.5" />
              <TablerIcon class="dx-v2-rail-tools-icon" name="hexagon" size={16} stroke-width="1.5" />
              <span class="dx-v2-rail-label">{language.t("datox.designer.workspaceTools")}</span>
              <TablerIcon class="dx-v2-rail-chevron" name={toolsOpen() ? "chevron-down" : "chevron-right"} size={14} stroke-width="1.5" />
            </button>
          </TooltipV2>
          <Show when={toolsOpen()}>
            <div class="dx-v2-rail-group" role="group" aria-label={language.t("datox.designer.workspaceTools")}>
              <For each={sections()}>
                {(section, index) => (
                  <>
                    <span class="dx-v2-rail-caption" data-lead={index() === 0 || undefined} role="separator" aria-label={section.title}>{section.title}</span>
                    <For each={section.items}>{rail}</For>
                  </>
                )}
              </For>
            </div>
          </Show>
          <span class="dx-v2-rail-spring" />
          <DatoxV2Profile expanded={expanded()} />
        </nav>
      </Show>
      <div class="dx-v2-main">
        <header
          class="dx-v2-topbar"
          data-desktop={desktop || undefined}
          data-tauri-drag-region={desktop || undefined}
          style={{
            "min-height": desktop ? `${TITLEBAR_HEIGHT / zoom()}px` : undefined,
            "padding-left": lights() ? `${Math.max(0, MAC_TRAFFIC_LIGHTS_WIDTH - (focus() ? 0 : railWidth())) / zoom()}px` : undefined,
            "padding-right": windows ? `${WINDOWS_CONTROLS_WIDTH / Math.max(zoom(), 1)}px` : undefined,
          }}
        >
          <span />
          <div id="opencode-titlebar-center" class="dx-v2-topbar-slot dx-v2-topbar-slot--center" />
          <div class="dx-v2-topbar-end">
            <Show when={updateVersion() !== undefined || installing()}>
              <button
                type="button"
                class="dx-v2-update"
                onClick={() => props.update?.install()}
                disabled={installing()}
                aria-busy={installing()}
                aria-label={language.t("toast.update.action.installRestart")}
                title={updateVersion() ? language.t("titlebar.updateVersion", { version: updateVersion()! }) : undefined}
              >
                <TablerIcon name="download" size={12} />
                {language.t("titlebar.update")}
              </button>
            </Show>
            <div id="opencode-titlebar-right" class="dx-v2-topbar-slot" />
          </div>
        </header>
        <Suspense>{props.children}</Suspense>
      </div>
    </div>
  )
}

// The rail avatar opens the account menu: the items and actions of Titlebar's DatoxTitlebarProfile, drawn as a V2 menu.
// Also mounted at the foot of the report editor's tool rail, where the shell rail is hidden. In the expanded sidebar it is
// the account row (initials, name, company) and the menu opens above it.
export function DatoxV2Profile(props: { expanded?: boolean }) {
  const auth = useDatoxAuth()
  const language = useLanguage()
  const dialog = useDialog()
  const openSettings = useSettingsCommand()
  const [profile, setProfile] = createStore({ open: false, companies: [] as DatoxCompany[] })
  const open = () => profile.open
  const setOpen = (value: boolean) => setProfile("open", value)
  // A plain promise rather than a resource: a resource read here would suspend (and on error, throw through) the whole shell.
  const companies = () => profile.companies
  let asked = false
  createEffect(() => {
    if (asked || !auth.authenticated()) return
    asked = true
    listCompanies().then((items) => setProfile("companies", items)).catch(() => {})
  })
  let root: HTMLDivElement | undefined
  let trigger: HTMLButtonElement | undefined

  const userLabel = (): string =>
    auth.state.accountName || auth.state.accountEmail || localDevHeaders()["X-User-Email"] || localDevHeaders()["X-User-Id"] || ""
  const companyLabel = () => {
    const company = companies().find((item) => item.id === activeCompanyID())
    if (!company || company.name === "Local Development Company") return language.t("datox.company.default")
    return company.name
  }
  const initials = () =>
    userLabel()
      .split(/[\s.@]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part[0]!.toUpperCase())
      .join("") || "U"
  const run = (action: () => void) => () => {
    setOpen(false)
    action()
  }
  const logout = () =>
    dialog.show(() => (
      <DialogDatoxLogout
        user={userLabel()}
        onLogout={() => {
          localStorage.removeItem("activeCompanyId")
          localStorage.removeItem("datox.agent.companyId")
          localStorage.removeItem("datox.agent.userId")
          localStorage.removeItem("datox.agent.userEmail")
          sessionStorage.clear()
          window.location.reload()
        }}
      />
    ))

  makeEventListener(document, "pointerdown", (event) => {
    if (open() && root && !root.contains(event.target as Node)) setOpen(false)
  })
  makeEventListener(document, "keydown", (event) => {
    if (!open() || event.key !== "Escape") return
    event.preventDefault()
    setOpen(false)
    trigger?.focus()
  })

  return (
    <div ref={root} class="dx-v2-rail-menu" data-expanded={props.expanded || undefined}>
      <TooltipV2 placement="right" value={userLabel()} inactive={props.expanded}>
        <button
          ref={trigger}
          type="button"
          class="dx-v2-account"
          aria-label={userLabel()}
          aria-haspopup="menu"
          aria-expanded={open()}
          onClick={() => setOpen(!open())}
        >
          <span class="dx-v2-rail-avatar">{initials()}</span>
          <Show when={props.expanded}>
            <span class="dx-v2-account-text">
              <b>{userLabel()}</b>
              <span>{companyLabel()}</span>
            </span>
            <TablerIcon class="dx-v2-account-more" name="dots" size={16} stroke-width="1.5" />
          </Show>
        </button>
      </TooltipV2>
      <Show when={open()}>
        <div class="dx-v2-menu" role="menu" aria-label={userLabel()}>
          <div class="dx-v2-menu-section">
            <div class="dx-v2-menu-head">
              <b>{userLabel()}</b>
              <span class="dx-v2-menu-caption">{companyLabel()}</span>
            </div>
          </div>
          <div class="dx-v2-menu-section">
            <button type="button" role="menuitem" class="dx-v2-menu-item" onClick={run(() => dialog.show(() => <DialogDatoxActivityLogs />))}>
              <TablerIcon name="activity" size={16} />
              {language.t("datox.activity.menu")}
            </button>
            <button type="button" role="menuitem" class="dx-v2-menu-item" onClick={run(() => dialog.show(() => <DialogDatoxUsage />))}>
              <TablerIcon name="chart-bar" size={16} />
              {language.t("datox.usage.menu")}
            </button>
            <button type="button" role="menuitem" class="dx-v2-menu-item" onClick={run(openSettings)}>
              <TablerIcon name="settings" size={16} />
              {language.t("sidebar.settings")}
            </button>
          </div>
          <div class="dx-v2-menu-section">
            <button type="button" role="menuitem" class="dx-v2-menu-item" onClick={run(logout)}>
              <TablerIcon name="logout" size={16} />
              {language.t("datox.profile.logout")}
            </button>
          </div>
        </div>
      </Show>
    </div>
  )
}
